/**
 * Read a plain string answer from Ant Design form `responses` (array or map).
 */
export const getFormResponseAtIndex = (responses, index) => {
  if (responses == null) return null;

  if (Array.isArray(responses)) {
    return responses[index] ?? null;
  }

  if (typeof responses === "object") {
    const value = responses[index] ?? responses[String(index)];
    return value === undefined ? null : value;
  }

  return null;
};

const asPlainText = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "object") return null;

  const text = String(value).trim();
  if (!text) return null;

  const normalized = text.toLowerCase();
  // Treat UI/placeholder defaults as empty — not real answers
  if (
    normalized === "anonymous" ||
    normalized === "anonymous user" ||
    normalized === "n/a" ||
    normalized === "na"
  ) {
    return null;
  }

  return text;
};

/**
 * Pull name/email from question inputs by input_type (not by field index).
 */
export const extractContactFromQuestionValues = (question, values = {}) => {
  let fullName = null;
  let email = null;

  const inputs = Array.isArray(question?.response) ? question.response : [];

  inputs.forEach((input, index) => {
    const raw = getFormResponseAtIndex(values.responses, index);
    const text = asPlainText(raw);

    if (!text) return;

    if (input?.input_type === "name" && !fullName) {
      fullName = text;
    }

    if (input?.input_type === "email" && !email) {
      email = text;
    }
  });

  return { fullName, email };
};

/**
 * Pull name/email from a stored response map ({ [title]: { type, value } }).
 */
export const extractContactFromStoredResponses = (responses = {}) => {
  if (!responses || typeof responses !== "object") {
    return { fullName: null, email: null };
  }

  let fullName = null;
  let email = null;

  Object.values(responses).forEach((entry) => {
    if (!entry || typeof entry !== "object") return;

    const text = asPlainText(entry.value);
    if (!text) return;

    const type = String(entry.type || "")
      .trim()
      .toLowerCase();

    if (type === "name" && !fullName) {
      fullName = text;
    }

    if (type === "email" && !email) {
      email = text;
    }
  });

  return { fullName, email };
};

export const readSurveyUserData = () => {
  try {
    const raw = localStorage.getItem("surveyUserData");
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

/**
 * Build auth/register payload from the current question answers,
 * falling back to intro surveyUserData when present.
 */
export const buildSurveyUserInfo = (question, values = {}) => {
  const fromQuestion = extractContactFromQuestionValues(question, values);
  const fromIntro = readSurveyUserData();

  const fullName =
    fromQuestion.fullName ||
    asPlainText(fromIntro?.fullName) ||
    asPlainText(fromIntro?.full_name) ||
    null;

  const email =
    fromQuestion.email ||
    asPlainText(fromIntro?.email) ||
    null;

  return {
    full_name: fullName,
    email: email,
  };
};
